import React from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import PageHeader from '../../components/common/PageHeader';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Clock, BookOpen } from 'lucide-react';

const LearnCatalog: React.FC = () => {
  const modules = [
    { id: 1, title: 'AI Basics for SMEs', level: 'Novice', duration: 12, sector: 'Core' },
    { id: 2, title: 'Responsible Use (NZ Privacy Act)', level: 'Novice', duration: 10, sector: 'Core' },
    { id: 3, title: 'Prompt Hygiene & Output QA', level: 'Practitioner', duration: 12, sector: 'Core' },
    { id: 4, title: 'From Notes to SOP', level: 'Practitioner', duration: 11, sector: 'Core' },
    { id: 5, title: 'Sector-Specific Lenses', level: 'Practitioner', duration: 15, sector: 'Optional' },
    { id: 6, title: 'Use-Case Discovery & ROI', level: 'Practitioner', duration: 12, sector: 'Core' },
    { id: 7, title: 'Choosing & Governing Tools', level: 'Advanced', duration: 13, sector: 'Core' },
    { id: 8, title: 'Data Hygiene', level: 'Advanced', duration: 11, sector: 'Core' },
    { id: 9, title: 'Change Management', level: 'Advanced', duration: 12, sector: 'Core' },
    { id: 10, title: 'Capstone Projects Primer', level: 'Advanced', duration: 10, sector: 'Core' },
  ];

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <PageHeader 
          title="Course Catalog"
          subtitle="16 comprehensive modules covering AI adoption from basics to implementation"
        />
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="mb-12 text-center">
              <p className="text-gray-700 max-w-3xl mx-auto">
                Our self-paced online program includes 10 core modules, 6 automation pattern guides, 
                and 2 capstone projects. All content is WCAG 2.1 AA accessible with captions, transcripts, 
                and downloadable materials.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {modules.map((module) => (
                <Card key={module.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-brand-teal" />
                      <h3 className="text-lg font-semibold text-brand-navy">
                        Module {module.id}
                      </h3>
                    </div>
                    <Badge variant={module.level === 'Novice' ? 'secondary' : module.level === 'Practitioner' ? 'default' : 'destructive'}>
                      {module.level}
                    </Badge>
                  </div>
                  
                  <h4 className="text-xl font-semibold mb-3 text-gray-800">
                    {module.title}
                  </h4>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{module.duration} minutes</span>
                    </div>
                    <Badge variant="outline">{module.sector}</Badge>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-12 bg-brand-teal/10 rounded-lg p-8 border border-brand-teal/20">
              <h3 className="text-2xl font-bold mb-4 text-brand-navy">Automation Patterns</h3>
              <p className="text-gray-700 mb-6">
                In addition to the core modules, you'll learn 6 proven automation patterns, 
                each with variants for Microsoft, Google, Xero, and GoHighLevel:
              </p>
              <ul className="grid sm:grid-cols-2 gap-4">
                <li className="flex items-start">
                  <span className="text-brand-teal mr-2">✓</span>
                  <span className="text-gray-700">P0: Preflight Safety Checklist</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-teal mr-2">✓</span>
                  <span className="text-gray-700">P1: Email Triage Automation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-teal mr-2">✓</span>
                  <span className="text-gray-700">P2: Lead Capture & CRM Auto-Reply</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-teal mr-2">✓</span>
                  <span className="text-gray-700">P3: Meeting Notes to SOP Draft</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-teal mr-2">✓</span>
                  <span className="text-gray-700">P4: Proposal/Quote Generation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-teal mr-2">✓</span>
                  <span className="text-gray-700">P5: Ticket/Issue Triage</span>
                </li>
              </ul>
            </div>

            <div className="mt-12 bg-gray-50 rounded-lg p-8 border border-gray-200">
              <h3 className="text-2xl font-bold mb-4 text-brand-navy">Capstone Projects</h3>
              <p className="text-gray-700 mb-6">
                Complete two real-world projects to earn your Chamber-endorsed certificate:
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-brand-navy mb-2">1. AI Policy Document</h4>
                  <p className="text-gray-700 text-sm">
                    Create a 1-page AI policy for your business using our template. 
                    Learn to set clear guidelines for AI use, data handling, and client disclosure.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-brand-navy mb-2">2. Live Workflow Implementation</h4>
                  <p className="text-gray-700 text-sm">
                    Implement one automation pattern in your actual business. 
                    Document your process, measure ROI, and prove real-world impact.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default LearnCatalog;
